import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams } from 'react-router-dom';
import { TimeField, formatStamp } from '../lib/timeFields.jsx';
import { useVenueTz } from '@/lib/venueTz.js';

export default function TenantSettings() {
  const { tenantSlug } = useParams();
  const tz = useVenueTz();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    apiFetch(`/api/tenants/${tenantSlug}/settings`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setData(d); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tenantSlug]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setDraft(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (error) return <div className="text-destructive">Error: {error}</div>;
  if (!data) return null;

  const t = data.tenant;
  const editing = !!draft;
  const v = editing ? draft : t;
  const setField = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));
  const startEdit = () => setDraft({ ...t });

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Settings · {t.slug}</h1>
        {editing ? (
          <div className="flex gap-2">
            <button onClick={() => setDraft(null)} disabled={saving} className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent disabled:opacity-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        ) : (
          <button onClick={startEdit} className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent">Edit</button>
        )}
      </div>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Brand</h2>
        <div className="border border-border rounded-md p-3 bg-card grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <Field label="Name">{editing ? <Input value={v.name} onChange={setField('name')} /> : v.name}</Field>
          <Field label="Status"><span className="capitalize">{v.status}</span></Field>
          <Field label="Plan"><span className="capitalize">{v.plan}</span></Field>
          <Field label="Brand color">{editing ? <Input value={v.brand_color || ''} onChange={setField('brand_color')} placeholder="#ff7a00" /> : v.brand_color || '—'}</Field>
          <Field label="Logo URL">{editing ? <Input value={v.logo_url || ''} onChange={setField('logo_url')} /> : v.logo_url || '—'}</Field>
          <Field label="Favicon">
            <div className="flex items-center gap-2">
              {(editing ? v.favicon_url : t.favicon_url) && (
                <img src={editing ? v.favicon_url : t.favicon_url} alt="" className="w-5 h-5 rounded" />
              )}
              {editing ? (
                <>
                  <Input value={v.favicon_url || ''} onChange={setField('favicon_url')} placeholder="/uploads/… or URL" />
                  <label className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent cursor-pointer whitespace-nowrap">
                    Upload
                    <input
                      type="file" accept="image/png,image/webp,image/jpeg" className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await apiFetch(`/api/tenants/${tenantSlug}/favicon`, { method: 'PUT', body: fd });
                        const d = await res.json();
                        if (res.ok && d.url) setDraft((prev) => ({ ...prev, favicon_url: d.url }));
                        else setError(d.detail || d.error || 'upload failed');
                      }}
                    />
                  </label>
                </>
              ) : (v.favicon_url || '—')}
            </div>
            {editing && <div className="text-[10px] text-muted-foreground mt-1">Square PNG, 180px+. Browser tab + home-screen icon.</div>}
          </Field>
        </div>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Universe-widget</h2>
        <div className="border border-border rounded-md p-3 bg-card grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <Field label="uses_universe_widget">
            {editing ? (
              <input type="checkbox" checked={!!v.uses_universe_widget} onChange={setField('uses_universe_widget')} />
            ) : (v.uses_universe_widget ? 'YES' : 'no')}
          </Field>
          <Field label="universe_widget_host_key">
            {editing ? <Input value={v.universe_widget_host_key || ''} onChange={setField('universe_widget_host_key')} placeholder="liv" /> : v.universe_widget_host_key || '—'}
          </Field>
        </div>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">AI description template</h2>
        <div className="border border-border rounded-md p-3 bg-card">
          {editing ? (
            <textarea
              value={v.event_description_prompt_template || ''}
              onChange={setField('event_description_prompt_template')}
              rows={12}
              className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono text-xs"
              placeholder="System prompt for the Generate description button…"
            />
          ) : (
            <pre className="whitespace-pre-wrap text-xs text-muted-foreground max-h-60 overflow-y-auto">{v.event_description_prompt_template || '(none, Generate button will 400)'}</pre>
          )}
          <div className="text-xs text-muted-foreground mt-2">
            {v.event_description_prompt_template ? `${v.event_description_prompt_template.length} chars` : 'empty'}
          </div>
        </div>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Platforms</h2>
        <PlatformsGrid
          creds={data.platform_credentials}
          tenantSlug={tenantSlug}
          canManageCredentials={data.can_manage_credentials}
          onChanged={load}
        />
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-1">Universe defaults</h2>
        <p className="text-xs text-muted-foreground mb-2">Inherited by every event unless overridden per-event in the event's Universe editor. On Universe these all live on the listing, this is just the default layer.</p>
        <UniverseVenuesSection tenantSlug={tenantSlug} />
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">UrVenue venue config</h2>
        <UrVenueVenuesSection tenantSlug={tenantSlug} />
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">JamBase reaper</h2>
        <JambaseVenuesSection tenantSlug={tenantSlug} />
      </section>

      <section>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Raw credentials ({data.platform_credentials.length})</h2>
        <div className="border border-border rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Platform</th>
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Last used</th>
                <th className="px-3 py-2 font-medium">Token expires</th>
              </tr>
            </thead>
            <tbody>
              {data.platform_credentials.map((c, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-3 py-2 capitalize">{c.platform}</td>
                  <td className="px-3 py-2 text-muted-foreground font-mono text-xs">{c.account_id || '—'}</td>
                  <td className="px-3 py-2">{c.status}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{c.last_used_at ? formatStamp(c.last_used_at, tz) : '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{c.token_expires_at ? formatStamp(c.token_expires_at, tz) : '—'}</td>
                </tr>
              ))}
              {data.platform_credentials.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No platforms connected yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const PLATFORM_CATALOG = [
  // Ticketing — GA inventory, slice + sold-count monitoring
  { key: 'universe',     label: 'Universe',          category: 'Ticketing',   auth: 'oauth',          status: 'active',  primary: true },
  { key: 'dice',         label: 'Dice',              category: 'Ticketing',   auth: 'api_key',        status: 'coming',  note: 'Partner application + API key required' },
  { key: 'speakeasy',    label: 'Speakeasy',         category: 'Ticketing',   auth: 'api_key',        status: 'coming' },
  { key: 'ticketweb',    label: 'TicketWeb',         category: 'Ticketing',   auth: 'username_password', status: 'active', note: 'Shared across LIV Miami and Gekko Miami' },
  { key: 'tixr',         label: 'Tixr',              category: 'Ticketing',   auth: 'api_key',        status: 'coming' },
  { key: 'ticketmaster', label: 'Ticketmaster',      category: 'Ticketing',   auth: 'api_key',        status: 'coming', note: 'Partnership-gated' },
  // Tables / VIP — own inventory + receives primary ticketing URL
  { key: 'urvenue',      label: 'UrVenue',           category: 'Tables/VIP',  auth: 'service_account', status: 'active' },
  // Discovery — no inventory; receives metadata + primary ticketing URL
  { key: 'bandsintown',     label: 'Bandsintown',       category: 'Discovery',    auth: 'cognito', status: 'active' },
  { key: 'resident_advisor',label: 'Resident Advisor',  category: 'Discovery',    auth: 'api_key', status: 'coming' },
  { key: 'jambase',         label: 'JamBase',           category: 'Discovery',    auth: 'wp_login', status: 'active', note: 'Reaper, auto-deletes spam events JamBase scrapes onto our venues' },
  // Creative — separate workstream
  { key: 'monday_video_promo', label: 'Monday · Video Promo', category: 'Creative', auth: 'api_key', status: 'coming' },
  { key: 'monday_video_ad',    label: 'Monday, Video Ad',    category: 'Creative', auth: 'api_key', status: 'coming' },
  { key: 'monday_ad',          label: 'Monday, Display Ad',  category: 'Creative', auth: 'api_key', status: 'coming' },
  { key: 'meta',               label: 'Meta',                  category: 'Creative', auth: 'oauth',   status: 'coming', note: 'Marketing API, audience sync + ad-set push' },
];

const CATEGORY_ORDER = ['Ticketing', 'Tables/VIP', 'Discovery', 'Creative'];

function PlatformsGrid({ creds, tenantSlug, canManageCredentials, onChanged }) {
  // Index creds by platform — for monday_*, also match base 'monday'.
  const byPlatform = new Map();
  for (const c of creds || []) {
    byPlatform.set(c.platform, c);
  }
  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: PLATFORM_CATALOG.filter((p) => p.category === cat),
  }));
  return (
    <div className="space-y-4">
      {grouped.map(({ cat, items }) => (
        <div key={cat}>
          <div className="text-xs font-medium text-muted-foreground mb-2">{cat}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((p) => (
              <PlatformCard
                key={p.key}
                def={p}
                cred={byPlatform.get(p.key)}
                tenantSlug={tenantSlug}
                canManageCredentials={canManageCredentials}
                onChanged={onChanged}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Platforms whose Connect/Reconnect flow is actually wired (accepts a pasted
// credential). Others still fall back to the manual-seed notice.
const CONNECT_WIRED = new Set(['bandsintown', 'ticketweb']);

function PlatformCard({ def, cred, tenantSlug, canManageCredentials, onChanged }) {
  const tz = useVenueTz();
  const [modal, setModal] = useState(null); // null | 'connect' | 'edit' | 'disconnect' | 'info'
  const isComing = def.status === 'coming';
  const isConnected = cred && cred.status === 'active';
  const isExpired = cred && cred.status === 'expired';
  const isRevoked = cred && cred.status === 'revoked';
  const wired = CONNECT_WIRED.has(def.key);
  const isTicketWeb = def.key === 'ticketweb';

  let statusBadge;
  if (isComing) statusBadge = <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Coming soon</span>;
  else if (isExpired) statusBadge = <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300">Expired</span>;
  else if (isRevoked) statusBadge = <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/15 text-destructive">Revoked</span>;
  else if (isConnected) statusBadge = <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">Connected</span>;
  else statusBadge = <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Not connected</span>;

  // Connect/Reconnect needs a token flow → wired platforms only. Edit (config /
  // label) and Disconnect work for any existing credential.
  const connectLabel = isExpired || isRevoked ? 'Reconnect' : 'Connect';
  const btnBase = 'px-2 py-1 text-xs border rounded-md disabled:opacity-50';

  return (
    <div className={`border border-border rounded-md p-3 bg-card ${isComing ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="font-medium text-sm flex items-center gap-1.5">
          {def.label}
          {def.primary && <span className="text-[10px] px-1 rounded bg-primary/10 text-primary">primary</span>}
        </div>
        {statusBadge}
      </div>
      <div className="text-[11px] text-muted-foreground mb-2">{def.auth.replace('_', ' ')}</div>
      {cred && (
        <div className="text-[11px] text-muted-foreground space-y-0.5 mb-2">
          {cred.account_id && <div className="font-mono truncate">{cred.account_id}</div>}
          {cred.last_used_at && <div>last used {formatStamp(cred.last_used_at, tz)}</div>}
          {cred.token_expires_at && <div>expires {formatStamp(cred.token_expires_at, tz)}</div>}
        </div>
      )}
      {def.note && <div className="text-[10px] text-muted-foreground italic mb-2">{def.note}</div>}

      {isTicketWeb && !canManageCredentials ? (
        <div className="text-[10px] text-muted-foreground">Admin access is required to update credentials.</div>
      ) : isComing ? (
        <button disabled title="Not yet available" className={`${btnBase} w-full border-input text-muted-foreground cursor-not-allowed`}>
          Connect
        </button>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {/* Connect / Reconnect, wired (token) platforms; info notice otherwise. */}
          {!isConnected && (
            wired
              ? <button onClick={() => setModal('connect')} className={`${btnBase} border-primary text-primary hover:bg-primary/10`}>{connectLabel}</button>
              : <button onClick={() => setModal('info')} className={`${btnBase} border-input hover:bg-accent`}>{connectLabel}</button>
          )}
          {wired && isConnected && (
            <button onClick={() => setModal('connect')} className={`${btnBase} border-input hover:bg-accent`}>
              {isTicketWeb ? 'Update credentials' : 'Reconnect'}
            </button>
          )}
          {cred && !isTicketWeb && (
            <button onClick={() => setModal('edit')} className={`${btnBase} border-input hover:bg-accent`}>Edit</button>
          )}
          {isConnected && !isTicketWeb && (
            <button onClick={() => setModal('disconnect')} className={`${btnBase} border-input hover:bg-accent text-destructive`}>Disconnect</button>
          )}
        </div>
      )}

      {modal && (
        <PlatformCredModal
          def={def}
          cred={cred}
          mode={modal}
          wired={wired}
          tenantSlug={tenantSlug}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); onChanged?.(); }}
        />
      )}
    </div>
  );
}

// Connect / Reconnect / Edit / Disconnect dialog for a platform credential.
// In-UI only — no window.alert/confirm. For bandsintown the primary path is
// email + password (Cognito SRP, server-side); the password can be saved
// encrypted so the system auto-reconnects when the token later expires. A
// pasted refresh token is the fallback / first-time bootstrap that captures the
// User Pool ID needed for password login.
function PlatformCredModal({ def, cred, mode, wired, tenantSlug, onClose, onSaved }) {
  const isBandsintown = def.key === 'bandsintown';
  const isTicketWeb = def.key === 'ticketweb';
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [storePassword, setStorePassword] = useState(true);
  const [refreshToken, setRefreshToken] = useState('');
  const [poolId, setPoolId] = useState('');
  const [accountId, setAccountId] = useState(cred?.account_id || '');
  const [config, setConfig] = useState(null);     // structured config (edit)
  const [meta, setMeta] = useState(null);         // { has_password, has_user_pool_id, config.login_email }
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Load safe config for connect (need has_user_pool_id / login_email) and edit.
  useEffect(() => {
    if (mode === 'disconnect' || mode === 'info') return;
    let cancel = false;
    apiFetch(`/api/tenants/${tenantSlug}/platform-credentials/${def.key}/config`)
      .then((r) => r.json())
      .then((d) => {
        if (cancel) return;
        setMeta(d);
        setConfig(d.config || {});
        if (d.account_id != null) setAccountId(d.account_id);
        if (d.config?.login_email) setEmail(d.config.login_email);
        if (d.config?.login_user) setUsername(d.config.login_user);
        if (d.config?.user_pool_id) setPoolId(d.config.user_pool_id);
      })
      .catch(() => { if (!cancel) { setMeta({}); setConfig({}); } });
    return () => { cancel = true; };
  }, [mode, def.key, tenantSlug]);

  const setCfg = (k) => (e) => setConfig((c) => ({ ...(c || {}), [k]: e.target.value }));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      if (mode === 'disconnect') {
        const r = await apiFetch(`/api/tenants/${tenantSlug}/platform-credentials/${def.key}/disconnect`, { method: 'POST' });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.detail || d.error || 'disconnect failed');
        onSaved();
        return;
      }

      const body = {};
      const extraCfg = {};
      if (poolId.trim()) extraCfg.user_pool_id = poolId.trim();

      if (isTicketWeb) {
        if (!username.trim()) throw new Error('Enter the TicketWeb username.');
        if (!password) throw new Error('Enter the new TicketWeb password.');
        body.username = username.trim();
        body.password = password;
      } else if (mode === 'connect') {
        if (email.trim() && password) {
          body.email = email.trim();
          body.password = password;
          body.store_password = storePassword;
        } else if (refreshToken.trim()) {
          body.refresh_token = refreshToken.trim();
        } else {
          throw new Error('Enter the Bandsintown email + password, or paste a refresh token.');
        }
        if (Object.keys(extraCfg).length) body.config = extraCfg;
      } else if (mode === 'edit') {
        body.account_id = accountId;
        if (email.trim() && password) { body.email = email.trim(); body.password = password; body.store_password = storePassword; }
        else if (refreshToken.trim()) body.refresh_token = refreshToken.trim();
        body.config = { ...(config || {}), ...extraCfg };
      }

      const r = await apiFetch(`/api/tenants/${tenantSlug}/platform-credentials/${def.key}/connect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || d.error || 'save failed');
      onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const title = isTicketWeb ? 'Update TicketWeb credentials'
    : mode === 'disconnect' ? `Disconnect ${def.label}`
    : mode === 'edit' ? `Edit ${def.label}`
    : mode === 'info' ? def.label
    : (cred ? `Reconnect ${def.label}` : `Connect ${def.label}`);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-md p-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground px-1">✕</button>
        </div>

        {mode === 'info' && (
          <p className="text-sm text-muted-foreground">
            The connect flow for {def.label} isn’t wired yet, its credentials are seeded manually
            ({def.auth.replace('_', ' ')}). Use Edit to adjust the label, or contact an admin to (re)connect.
          </p>
        )}

        {mode === 'disconnect' && (
          <p className="text-sm text-muted-foreground">
            This marks {def.label} as <span className="font-medium">revoked</span> for this tenant, pushes and
            listing reads stop until you reconnect. The stored token isn’t deleted.
          </p>
        )}

        {(mode === 'connect' || mode === 'edit') && isTicketWeb && (
          <div className="space-y-3 mb-3">
            <div className="rounded-md border border-border bg-muted/40 p-2 text-xs text-muted-foreground">
              This is the shared TicketWeb account for LIV Miami and Gekko Miami. Saving here updates both venues immediately.
            </div>
            <Field label="Username">
              <Input
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="TicketWeb username"
              />
            </Field>
            <Field label="New password">
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </Field>
            <Field label="Organization ID">
              <Input value={meta?.config?.org_id || accountId || '381363'} readOnly className="font-mono text-muted-foreground" />
            </Field>
            <p className="text-[10px] text-muted-foreground">
              The password is verified with TicketWeb before it is encrypted and saved. Existing credentials remain active if verification fails.
              {meta?.has_password && <span className="text-emerald-600 dark:text-emerald-400"> · Stored credential active</span>}
            </p>
          </div>
        )}

        {/* Email + password (Cognito SRP), primary path for bandsintown. */}
        {(mode === 'connect' || mode === 'edit') && isBandsintown && (
          <div className="space-y-2 mb-3">
            {mode === 'edit' && <div className="text-xs font-medium text-muted-foreground">Bandsintown login</div>}
            <Field label="Email">
              <Input type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="manager@livnightclub.com" />
            </Field>
            <Field label={mode === 'edit' ? 'Password (leave blank to keep current)' : 'Password'}>
              <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </Field>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={storePassword} onChange={(e) => setStorePassword(e.target.checked)} />
              Save password (encrypted) so listings auto-reconnect when the token expires
              {meta?.has_password && <span className="text-emerald-600 dark:text-emerald-400">· saved</span>}
            </label>
            <p className="text-[10px] text-muted-foreground">
              Just your Bandsintown login. The Cognito pool/client config is fetched automatically.
            </p>
          </div>
        )}

        {/* Advanced: refresh-token fallback + pool id + config. */}
        {(mode === 'connect' || mode === 'edit') && isBandsintown && (
          <div className="mb-3">
            <button type="button" onClick={() => setShowAdvanced((s) => !s)} className="text-xs text-muted-foreground hover:text-foreground">
              {showAdvanced ? '▾' : '▸'} Advanced (refresh token / pool id{mode === 'edit' ? ' / config' : ''})
            </button>
            {showAdvanced && (
              <div className="mt-2 space-y-2">
                <Field label="User Pool ID">
                  <Input value={poolId} onChange={(e) => setPoolId(e.target.value)} placeholder="us-east-1_XXXXXXXXX" />
                </Field>
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">Cognito refresh token (fallback / first-time)</div>
                  <textarea
                    value={refreshToken}
                    onChange={(e) => setRefreshToken(e.target.value)}
                    rows={3}
                    placeholder="venues.bandsintown.com → DevTools → Application → Local Storage → RefreshToken"
                    className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono text-[11px]"
                  />
                </div>
                {mode === 'edit' && config && (
                  <div className="grid grid-cols-2 gap-2">
                    {['manager_id', 'managed_actor_id', 'venue_id', 'cognito_client_id', 'fingerprint'].map((k) => (
                      <Field key={k} label={k}><Input value={config[k] ?? ''} onChange={setCfg(k)} /></Field>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Label edit (all platforms). */}
        {mode === 'edit' && !isTicketWeb && (
          <div className="mb-3">
            <Field label="Label (account_id)"><Input value={accountId} onChange={(e) => setAccountId(e.target.value)} /></Field>
            {!isBandsintown && <p className="text-[10px] text-muted-foreground mt-1">Only the label is editable here for {def.label}.</p>}
          </div>
        )}

        {err && <div className="text-xs text-destructive mb-2 break-words">{err}</div>}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent disabled:opacity-50">
            {mode === 'info' ? 'Close' : 'Cancel'}
          </button>
          {mode !== 'info' && (
            <button
              onClick={submit}
              disabled={busy}
              className={`px-3 py-1.5 text-sm rounded-md disabled:opacity-50 ${mode === 'disconnect' ? 'bg-destructive text-white hover:opacity-90' : 'bg-primary text-primary-foreground hover:opacity-90'}`}
            >
              {busy ? 'Saving…' : isTicketWeb ? 'Update credentials' : mode === 'disconnect' ? 'Disconnect' : mode === 'edit' ? 'Save' : (cred ? 'Reconnect' : 'Connect')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-0.5">{label}</div>
      <div>{children}</div>
    </div>
  );
}
function Input({ className = '', ...props }) {
  return <input {...props} className={`w-full px-2 py-1 border border-input rounded-md bg-background text-sm ${className}`} />;
}

// === UniverseVenuesSection =================================================
// Per-venue Universe calibration. The calibration probe reads a known-intact
// Universe listing and copies its address / lat-lon / category / refund
// policy / currency / tracking pixels into venues.integration_profile.universe.
// The values then flow through to every new event via the resolver service.

function UniverseVenuesSection({ tenantSlug }) {
  const [venues, setVenues] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    apiFetch(`/api/tenants/${tenantSlug}/universe/venues`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.detail || d.error); setVenues(d.venues); })
      .catch((e) => setError(e.message));
  };
  useEffect(load, [tenantSlug]);

  if (error) return <div className="text-xs text-destructive">{error}</div>;
  if (!venues) return <div className="text-xs text-muted-foreground">Loading venues…</div>;
  if (venues.length === 0) return <div className="text-xs text-muted-foreground">No venues for this tenant.</div>;

  return (
    <div className="space-y-3">
      {venues.map((v) => (
        <UniverseVenueRow key={v.id} tenantSlug={tenantSlug} venue={v} onSaved={load} />
      ))}
    </div>
  );
}

function UniverseVenueRow({ tenantSlug, venue, onSaved }) {
  const profile = venue.universe || {};
  const [listingId, setListingId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [showJson, setShowJson] = useState(false);

  const calibrate = async () => {
    if (!listingId.trim()) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/universe/calibrate-venue`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venue.id, listing_id: listingId.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'calibrate_failed');
      setOk(`Calibrated from ${listingId}`);
      setListingId('');
      onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const calibrated = !!profile.host_user_id;
  const tracking = profile.tracking || {};

  return (
    <div className="border border-border rounded-md p-3 bg-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-medium">{venue.name}</div>
          <div className="text-[10px] text-muted-foreground font-mono">{venue.slug}</div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded ${calibrated ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
          {calibrated ? '✓ calibrated' : '⚠ not calibrated'}
        </span>
      </div>

      {calibrated ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
          <div><span className="text-muted-foreground">host:</span> <code className="text-[10px]">{profile.host_user_id}</code></div>
          <div><span className="text-muted-foreground">category:</span> <code className="text-[10px]">{profile.category_id}</code></div>
          <div className="col-span-2"><span className="text-muted-foreground">address:</span> {profile.address}</div>
          <div><span className="text-muted-foreground">lat/lon:</span> {profile.latitude}, {profile.longitude}</div>
          <div><span className="text-muted-foreground">tz:</span> {profile.tz}</div>
          <div><span className="text-muted-foreground">currency:</span> {profile.transaction_currency}/{profile.settlement_currency}</div>
          <div><span className="text-muted-foreground">refund:</span> {profile.refund_policy?.offset} ({profile.refund_policy?.type})</div>
          <div><span className="text-muted-foreground">FB pixels:</span> {tracking.facebook_pixels?.length || 0}</div>
          <div><span className="text-muted-foreground">Google Ads:</span> {tracking.google_ads_trackers?.length || 0}</div>
          <div><span className="text-muted-foreground">GA4:</span> {tracking.google_analytics_4_id || '—'}</div>
          <div><span className="text-muted-foreground">waitlist:</span> {tracking.allow_waitlist ? '✓' : '—'}</div>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground mb-3">
          Calibrate this venue from a known-intact Universe listing to populate address, lat/lon, category, currency, refund policy, and tracking pixels.
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-xs text-muted-foreground mb-1">
            {calibrated ? 'Re-calibrate from' : 'Calibrate from'} Universe listing ID
          </label>
          <input
            value={listingId}
            onChange={(e) => setListingId(e.target.value)}
            placeholder="e.g. 696e656a0cbeef003995dbe5"
            className="w-full px-2 py-1 text-xs border border-input rounded-md bg-background font-mono"
          />
        </div>
        <button
          onClick={calibrate}
          disabled={busy || !listingId.trim()}
          className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
        >{busy ? 'Calibrating…' : (calibrated ? 'Re-calibrate' : 'Calibrate')}</button>
      </div>
      {err && <div className="text-xs text-destructive mt-2">{err}</div>}
      {ok && <div className="text-xs text-emerald-600 mt-2">{ok}</div>}

      {calibrated && (
        <>
          <div className="border-t border-border mt-3 pt-3 space-y-3">
            <VenueCategoryEditor
              tenantSlug={tenantSlug}
              venueId={venue.id}
              categoryId={profile.category_id}
              onSaved={onSaved}
            />
            <VenueTaxesEditor
              tenantSlug={tenantSlug}
              venueId={venue.id}
              taxes={profile.taxes || []}
              onSaved={onSaved}
            />
            <VenueFeesEditor
              tenantSlug={tenantSlug}
              venueId={venue.id}
              fees={profile.fees || []}
              onSaved={onSaved}
            />
          </div>
          <button
            onClick={() => setShowJson((s) => !s)}
            className="text-[10px] text-muted-foreground hover:text-foreground mt-3"
          >{showJson ? 'Hide' : 'Show'} raw JSON</button>
          {showJson && (
            <pre className="mt-2 p-2 bg-muted/30 rounded overflow-x-auto text-[10px]">
              {JSON.stringify(profile, null, 2)}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

// Category picker — venue.integration_profile.universe.category_id
// Pulls Universe's full category list (cached 6h server-side) and renders
// as a dropdown. Saves directly via venue PATCH.
function VenueCategoryEditor({ tenantSlug, venueId, categoryId, onSaved }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(categoryId || '');
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!editing) return;
    apiFetch(`/api/tenants/${tenantSlug}/universe/categories`)
      .then((r) => r.json())
      .then((d) => setList(d.categories || []))
      .catch((e) => setErr(e.message));
  }, [editing, tenantSlug]);
  useEffect(() => { if (!editing) setDraft(categoryId || ''); }, [categoryId, editing]);

  const save = async () => {
    setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe/venues/${venueId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category_id: draft || null }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || 'save_failed');
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) { setErr(e.message); }
  };

  const currentName = list?.find((c) => c.id === categoryId)?.name;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase text-muted-foreground">Universe category</div>
        {editing ? (
          <div className="flex gap-1">
            <button onClick={() => { setEditing(false); setDraft(categoryId || ''); }} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
            <button onClick={save} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">save</button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">edit</button>
        )}
      </div>
      {err && <div className="text-xs text-destructive mb-1">{err}</div>}
      {editing ? (
        list ? (
          <select value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full px-2 py-1 text-xs border border-input rounded-md bg-background">
            <option value="">· none —</option>
            {list.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        ) : (
          <div className="text-xs text-muted-foreground">Loading categories…</div>
        )
      ) : (
        <div className="text-xs">
          {currentName || categoryId
            ? <>{currentName || <em className="text-muted-foreground">unknown id</em>} <code className="text-[10px] text-muted-foreground ml-2">{categoryId}</code></>
            : <span className="text-muted-foreground italic">not set</span>}
        </div>
      )}
    </div>
  );
}

// Taxes editor — venue.integration_profile.universe.taxes[]
// Format mirrors Universe's taxes_attributes PUT /multiverse shape.
function VenueTaxesEditor({ tenantSlug, venueId, taxes, onSaved }) {
  const [draft, setDraft] = useState(taxes);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { if (!editing) setDraft(taxes); }, [taxes, editing]);

  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, { name: '', percent: 0, rate_ids: [] }]);

  const save = async () => {
    setErr(null);
    try {
      const clean = draft.filter((r) => r.name && r.name.trim()).map((r) => ({
        name: r.name.trim(),
        percent: Number(r.percent) || 0,
        rate_ids: r.rate_ids || [],
      }));
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe/venues/${venueId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taxes: clean }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || 'save_failed');
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase text-muted-foreground">Taxes ({(editing ? draft : taxes).length})</div>
        {editing ? (
          <div className="flex gap-1">
            <button onClick={add} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">+ tax</button>
            <button onClick={() => { setEditing(false); setDraft(taxes); }} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
            <button onClick={save} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">save</button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">edit</button>
        )}
      </div>
      {err && <div className="text-xs text-destructive mb-1">{err}</div>}
      {(editing ? draft : taxes).length === 0 && !editing ? (
        <div className="text-xs text-muted-foreground italic">No taxes configured.</div>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-[11px] border border-border rounded">
          <thead className="bg-muted/40 text-left text-muted-foreground">
            <tr><th className="px-2 py-1">name</th><th className="px-2 py-1 text-right">percent</th>{editing && <th></th>}</tr>
          </thead>
          <tbody>
            {(editing ? draft : taxes).map((r, i) => (
              <tr key={i} className="border-t border-border">
                {editing ? (
                  <>
                    <td className="px-1 py-1"><input value={r.name || ''} onChange={(e) => setRow(i, 'name', e.target.value)} className="w-full px-1 border border-input rounded bg-background" /></td>
                    <td className="px-1 py-1"><input type="number" step="0.01" value={r.percent ?? 0} onChange={(e) => setRow(i, 'percent', e.target.value)} className="w-20 px-1 border border-input rounded bg-background text-right" /></td>
                    <td className="px-1 py-1 text-right"><button onClick={() => remove(i)} className="text-destructive">×</button></td>
                  </>
                ) : (
                  <>
                    <td className="px-2 py-1">{r.name}</td>
                    <td className="px-2 py-1 text-right">{r.percent}%</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// === UrVenue venue config — mirror of UniverseVenuesSection ============
// Per-venue UrVenue defaults: event_type_id, default times, marketareaid,
// tz. Inherited by every event in the venue unless overridden at the
// template or per-event level.

function UrVenueVenuesSection({ tenantSlug }) {
  const [venues, setVenues] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    apiFetch(`/api/tenants/${tenantSlug}/urvenue/venues`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.detail || d.error); setVenues(d.venues); })
      .catch((e) => setError(e.message));
  };
  useEffect(load, [tenantSlug]);

  if (error) return <div className="text-xs text-destructive">{error}</div>;
  if (!venues) return <div className="text-xs text-muted-foreground">Loading venues…</div>;
  if (venues.length === 0) return <div className="text-xs text-muted-foreground">No venues for this tenant.</div>;

  return (
    <div className="space-y-3">
      {venues.map((v) => (
        <UrVenueVenueRow key={v.id} tenantSlug={tenantSlug} venue={v} onSaved={load} />
      ))}
    </div>
  );
}

function UrVenueVenueRow({ tenantSlug, venue, onSaved }) {
  const profile = venue.urvenue || {};
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    event_type_id: profile.event_type_id ?? '',
    default_start_time: profile.default_start_time ?? '',
    default_end_time: profile.default_end_time ?? '',
    default_doors_open: profile.default_doors_open ?? '',
    marketareaid: profile.marketareaid ?? '',
    tz: profile.tz ?? '',
    shifts: profile.shifts ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!editing) {
      setDraft({
        event_type_id: profile.event_type_id ?? '',
        default_start_time: profile.default_start_time ?? '',
        default_end_time: profile.default_end_time ?? '',
        default_doors_open: profile.default_doors_open ?? '',
        marketareaid: profile.marketareaid ?? '',
        tz: profile.tz ?? '',
        shifts: profile.shifts ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue, editing]);

  const configured = !!profile.event_type_id || !!profile.default_start_time;

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body = Object.fromEntries(
        Object.entries(draft).map(([k, v]) => [k, v && String(v).trim() ? v : null]),
      );
      const res = await apiFetch(`/api/tenants/${tenantSlug}/urvenue/venues/${venue.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || 'save_failed');
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border rounded-md p-3 bg-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-medium">{venue.name}</div>
          <div className="text-[10px] text-muted-foreground font-mono">{venue.slug}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-0.5 rounded ${configured ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
            {configured ? '✓ configured' : '⚠ not configured'}
          </span>
          {editing ? (
            <div className="flex gap-1">
              <button onClick={() => setEditing(false)} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
              <button onClick={save} disabled={busy} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded disabled:opacity-50">{busy ? 'saving…' : 'save'}</button>
            </div>
          ) : (
            <button onClick={() => setEditing(true)} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">edit</button>
          )}
        </div>
      </div>
      {err && <div className="text-xs text-destructive mb-2">{err}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <UrVenueRow label="event_type_id" editing={editing} value={draft.event_type_id} display={profile.event_type_id} mono
          onChange={(v) => setDraft((d) => ({ ...d, event_type_id: v }))} />
        <UrVenueRow label="marketareaid" editing={editing} value={draft.marketareaid} display={profile.marketareaid} mono
          onChange={(v) => setDraft((d) => ({ ...d, marketareaid: v }))} />
        <UrVenueRow label="default_start_time" editing={editing} value={draft.default_start_time} display={profile.default_start_time} time mono
          onChange={(v) => setDraft((d) => ({ ...d, default_start_time: v }))} />
        <UrVenueRow label="default_end_time" editing={editing} value={draft.default_end_time} display={profile.default_end_time} time mono
          onChange={(v) => setDraft((d) => ({ ...d, default_end_time: v }))} />
        <UrVenueRow label="default_doors_open" editing={editing} value={draft.default_doors_open} display={profile.default_doors_open} time mono
          onChange={(v) => setDraft((d) => ({ ...d, default_doors_open: v }))} />
        <UrVenueRow label="tz" editing={editing} value={draft.tz} display={profile.tz} placeholder="America/New_York" mono
          onChange={(v) => setDraft((d) => ({ ...d, tz: v }))} />
        <UrVenueRow label="shifts" editing={editing} value={draft.shifts} display={profile.shifts} mono
          onChange={(v) => setDraft((d) => ({ ...d, shifts: v }))} />
      </div>
    </div>
  );
}

function UrVenueRow({ label, editing, value, display, onChange, placeholder, mono, time }) {
  return (
    <div>
      <div className="text-muted-foreground text-[10px] mb-0.5">{label}</div>
      {editing && time ? (
        <TimeField
          value={value || ''}
          format="HH:MM:SS"
          onChange={(v) => onChange(v || '')}
          className="w-full px-2 py-1 border border-input rounded-md bg-background text-xs"
        />
      ) : editing ? (
        <input
          value={value || ''}
          placeholder={placeholder || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full px-2 py-1 border border-input rounded-md bg-background text-xs ${mono ? 'font-mono' : ''}`}
        />
      ) : display ? (
        <code className={mono ? 'text-[11px]' : 'text-xs'}>{String(display)}</code>
      ) : (
        <span className="text-muted-foreground italic text-xs">not set</span>
      )}
    </div>
  );
}

// Fees editor — venue.integration_profile.universe.fees[]
// Mirrors the cost.fees_attributes PUT /multiverse shape (each entry has
// name + flat + percent; Universe sums them per ticket).
function VenueFeesEditor({ tenantSlug, venueId, fees, onSaved }) {
  const [draft, setDraft] = useState(fees);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { if (!editing) setDraft(fees); }, [fees, editing]);

  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, { name: '', flat: 0, percent: 0, rate_ids: [] }]);

  const save = async () => {
    setErr(null);
    try {
      const clean = draft.filter((r) => r.name && r.name.trim()).map((r) => ({
        name: r.name.trim(),
        flat: Number(r.flat) || 0,
        percent: Number(r.percent) || 0,
        rate_ids: r.rate_ids || [],
      }));
      const res = await apiFetch(`/api/tenants/${tenantSlug}/universe/venues/${venueId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fees: clean }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || 'save_failed');
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase text-muted-foreground">Fees ({(editing ? draft : fees).length})</div>
        {editing ? (
          <div className="flex gap-1">
            <button onClick={add} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">+ fee</button>
            <button onClick={() => { setEditing(false); setDraft(fees); }} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
            <button onClick={save} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">save</button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">edit</button>
        )}
      </div>
      {err && <div className="text-xs text-destructive mb-1">{err}</div>}
      {(editing ? draft : fees).length === 0 && !editing ? (
        <div className="text-xs text-muted-foreground italic">No fees configured.</div>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-[11px] border border-border rounded">
          <thead className="bg-muted/40 text-left text-muted-foreground">
            <tr><th className="px-2 py-1">name</th><th className="px-2 py-1 text-right">flat $</th><th className="px-2 py-1 text-right">percent</th>{editing && <th></th>}</tr>
          </thead>
          <tbody>
            {(editing ? draft : fees).map((r, i) => (
              <tr key={i} className="border-t border-border">
                {editing ? (
                  <>
                    <td className="px-1 py-1"><input value={r.name || ''} onChange={(e) => setRow(i, 'name', e.target.value)} className="w-full px-1 border border-input rounded bg-background" /></td>
                    <td className="px-1 py-1"><input type="number" step="0.01" value={r.flat ?? 0} onChange={(e) => setRow(i, 'flat', e.target.value)} className="w-20 px-1 border border-input rounded bg-background text-right" /></td>
                    <td className="px-1 py-1"><input type="number" step="0.01" value={r.percent ?? 0} onChange={(e) => setRow(i, 'percent', e.target.value)} className="w-20 px-1 border border-input rounded bg-background text-right" /></td>
                    <td className="px-1 py-1 text-right"><button onClick={() => remove(i)} className="text-destructive">×</button></td>
                  </>
                ) : (
                  <>
                    <td className="px-2 py-1">{r.name}</td>
                    <td className="px-2 py-1 text-right">${r.flat}</td>
                    <td className="px-2 py-1 text-right">{r.percent}%</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// === JambaseVenuesSection ==================================================
// Per-venue JamBase reaper config. The reaper deletes events JamBase
// auto-imports (their JSON-LD MusicEvent pollutes Google Events with
// affiliate-redirect ticket URLs). Set the venue's jambase_venue_slug,
// flip enabled, optionally tweak the interval (defaults to 4h), then save.
// Until JAMBASE_REAPER_LIVE=true is set in the server .env, detected
// events log as `dry_run` instead of being deleted.
function JambaseVenuesSection({ tenantSlug }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    apiFetch(`/api/tenants/${tenantSlug}/jambase/venues`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.detail || d.error); setData(d); })
      .catch((e) => setError(e.message));
  };
  useEffect(load, [tenantSlug]);

  if (error) return <div className="text-xs text-destructive">{error}</div>;
  if (!data) return <div className="text-xs text-muted-foreground">Loading venues…</div>;

  return (
    <div className="space-y-3">
      <TenantReaperCard tenantSlug={tenantSlug} data={data} reload={load} />
      {data.venues.length === 0 && (
        <div className="border border-border rounded-md p-3 bg-card text-xs text-muted-foreground">
          No venues for this tenant.
        </div>
      )}
      {data.venues.map((v) => (
        <JambaseVenueRow key={v.id} tenantSlug={tenantSlug} venue={v} onSaved={load} />
      ))}
    </div>
  );
}

// Inline 2-column definition-list row. Uses the Universe/UrVenue idiom:
// muted-label · value cell. Set `mono` for monospace values; `editing` and
// `node` together swap the value cell into an input only when editing.
function DLRow({ label, children }) {
  return (
    <>
      <div className="text-muted-foreground">{label}</div>
      <div className="min-w-0">{children}</div>
    </>
  );
}

function TenantReaperCard({ tenantSlug, data, reload }) {
  const tz = useVenueTz();
  const { credential, reaper_live, reaper_state, notification_webhook_url } = data;
  const [busy, setBusy] = useState(false);
  const [editWebhook, setEditWebhook] = useState(false);
  const [webhookDraft, setWebhookDraft] = useState(notification_webhook_url || '');
  const [err, setErr] = useState(null);

  useEffect(() => { setWebhookDraft(notification_webhook_url || ''); }, [notification_webhook_url]);

  const patch = async (body) => {
    setBusy(true); setErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/jambase`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error);
      reload();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleLive = async () => {
    const next = !reaper_live;
    if (next && !confirm(`Enable LIVE deletes for ${tenantSlug}?\n\nThe reaper will start removing JamBase-imported events on every enabled venue at its scheduled interval.`)) return;
    await patch({ jambase_reaper_live: next });
  };

  const saveWebhook = async () => {
    await patch({ jambase_notification_webhook_url: webhookDraft.trim() || null });
    setEditWebhook(false);
  };

  return (
    <div className="border border-border rounded-md p-3 bg-card">
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium">Reaper settings</div>
        <span className={`text-[10px] px-2 py-0.5 rounded ${reaper_live
          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
          : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
          {reaper_live ? '● live deleting' : '○ dry-run only'}
        </span>
      </div>

      <div className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-1 text-xs">
        <DLRow label="live mode:">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={!!reaper_live} disabled={busy} onChange={toggleLive} />
            <span>{reaper_live ? 'enabled, deletes fire' : 'disabled, detections logged as dry-run'}</span>
          </label>
        </DLRow>
        <DLRow label="credential:">
          <span className="font-mono">{credential?.status || 'missing'}</span>
          {credential?.last_used_at && <span className="text-muted-foreground"> · last used {formatStamp(credential.last_used_at, tz)}</span>}
          {credential?.jwt_expires && <span className="text-muted-foreground"> · JWT exp {formatStamp(credential.jwt_expires, tz)}</span>}
        </DLRow>
        <DLRow label="session deletes:">
          <span className="font-mono">{reaper_state?.totalDeleted ?? 0}</span>
        </DLRow>
        <DLRow label="notification webhook:">
          {editWebhook ? (
            <div className="flex gap-1.5 items-center">
              <input
                type="url"
                value={webhookDraft}
                onChange={(e) => setWebhookDraft(e.target.value)}
                placeholder="https://hooks.slack.com/services/…"
                className="flex-1 px-2 py-1 border border-input rounded-md bg-background text-xs font-mono"
              />
              <button onClick={saveWebhook} disabled={busy} className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
              <button onClick={() => { setWebhookDraft(notification_webhook_url || ''); setEditWebhook(false); }} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Cancel</button>
            </div>
          ) : (
            <div className="flex gap-2 items-center">
              {notification_webhook_url
                ? <span className="font-mono truncate">{notification_webhook_url}</span>
                : <span className="text-muted-foreground italic">none</span>}
              <button onClick={() => setEditWebhook(true)} className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline shrink-0">
                {notification_webhook_url ? 'edit' : '+ add'}
              </button>
            </div>
          )}
        </DLRow>
      </div>

      {err && <div className="mt-2 text-xs text-destructive">{err}</div>}
    </div>
  );
}

// Vendor slugs we extract from `the_show.ticketing_links[].vendor.slug`.
// Snapshotted from observed JamBase events; "ticket-provider" is the
// generic venue-direct link (legitimate, usually NOT what we want to reap).
const KNOWN_VENDOR_SLUGS = [
  'viagogo', 'stubhub', 'gametime', 'seatgeek', 'ticketmaster',
  'tixr', 'dice', 'seated', 'eventbrite', 'universe',
  'ticket-provider', // venue-direct / generic
];

function ChipList({ items, emptyText }) {
  if (!items || items.length === 0) return <span className="text-xs text-muted-foreground italic">{emptyText}</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((s, i) => (
        <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-muted font-mono">{s}</span>
      ))}
    </div>
  );
}

function VendorChips({ selected, onChange }) {
  const sel = new Set(selected);
  const toggle = (slug) => {
    if (sel.has(slug)) onChange(selected.filter((s) => s !== slug));
    else onChange([...selected, slug]);
  };
  return (
    <div className="flex flex-wrap gap-1">
      {KNOWN_VENDOR_SLUGS.map((slug) => (
        <button
          key={slug}
          type="button"
          onClick={() => toggle(slug)}
          className={`text-[11px] px-1.5 py-0.5 rounded font-mono border ${
            sel.has(slug)
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background border-input hover:bg-accent'
          }`}
        >{slug}</button>
      ))}
      <div className="w-full text-[10px] text-muted-foreground mt-0.5">empty = delete every vendor</div>
    </div>
  );
}

function JambaseVenueRow({ tenantSlug, venue, onSaved }) {
  const tz = useVenueTz();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState(null);
  const [err, setErr] = useState(null);

  const startEdit = () => {
    setDraft({
      jambase_venue_slug: venue.jambase_venue_slug || '',
      jambase_venue_id: venue.jambase_venue_id || '',
      jambase_reaper_enabled: !!venue.jambase_reaper_enabled,
      jambase_reaper_interval_minutes: venue.jambase_reaper_interval_minutes || 240,
      jambase_headliner_allowlist: (venue.jambase_headliner_allowlist || []).join(', '),
      jambase_delete_only_if_vendor_in: venue.jambase_delete_only_if_vendor_in || [],
      jambase_only_within_days: venue.jambase_only_within_days ?? '',
    });
    setEditing(true);
    setErr(null);
  };
  const cancelEdit = () => { setEditing(false); setDraft(null); setErr(null); };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const body = { ...draft };
      if (body.jambase_venue_id === '') body.jambase_venue_id = null;
      // Normalize: comma-string → string[] for allowlist; empty window → null.
      if (typeof body.jambase_headliner_allowlist === 'string') {
        body.jambase_headliner_allowlist = body.jambase_headliner_allowlist
          .split(',').map((s) => s.trim()).filter(Boolean);
      }
      if (body.jambase_only_within_days === '' || body.jambase_only_within_days == null) {
        body.jambase_only_within_days = null;
      } else {
        body.jambase_only_within_days = Number(body.jambase_only_within_days);
      }
      const res = await apiFetch(`/api/tenants/${tenantSlug}/jambase/venues/${venue.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error);
      setEditing(false);
      setDraft(null);
      onSaved && onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/jambase/venues/${venue.id}/run`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error);
      onSaved && onSaved();
      if (showLog) loadLog();
    } catch (e) {
      setErr(e.message);
    } finally {
      setRunning(false);
    }
  };

  const loadLog = async () => {
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/jambase/venues/${venue.id}/log?limit=25`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error);
      setLog(d.entries);
    } catch (e) {
      setErr(e.message);
    }
  };
  const toggleLog = () => {
    if (!showLog && log === null) loadLog();
    setShowLog((v) => !v);
  };

  const enabled = venue.jambase_reaper_enabled;
  const configured = !!venue.jambase_venue_slug;
  const v = editing ? draft : venue;
  const intervalMin = v.jambase_reaper_interval_minutes || 240;

  return (
    <div className="border border-border rounded-md p-3 bg-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-medium">{venue.name}</div>
          <div className="text-[10px] text-muted-foreground font-mono">{venue.slug}</div>
        </div>
        <div className="flex items-center gap-2">
          {configured
            ? enabled
              ? <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">✓ reaping</span>
              : <span className="text-[10px] px-2 py-0.5 rounded bg-muted text-muted-foreground">configured, disabled</span>
            : <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300">⚠ no JamBase slug</span>}
          {!editing && (
            <>
              <button onClick={startEdit} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Edit</button>
              <button
                onClick={runNow}
                disabled={!configured || running}
                title={!configured ? 'Set jambase_venue_slug first' : 'Run a reap right now'}
                className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50"
              >{running ? 'Running…' : 'Run now'}</button>
              <button onClick={toggleLog} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">{showLog ? 'Hide log' : 'Show log'}</button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-1 text-xs">
        <DLRow label="jambase_venue_slug:">
          {editing
            ? <Input value={v.jambase_venue_slug} onChange={(e) => setDraft((d) => ({ ...d, jambase_venue_slug: e.target.value.trim() }))} placeholder="liv-nightclub-miami" />
            : v.jambase_venue_slug
              ? <span className="font-mono">{v.jambase_venue_slug}</span>
              : <span className="text-muted-foreground italic">unset</span>}
        </DLRow>
        <DLRow label="jambase_venue_id:">
          {editing
            ? <Input value={v.jambase_venue_id ?? ''} onChange={(e) => setDraft((d) => ({ ...d, jambase_venue_id: e.target.value.trim() }))} placeholder="14394826" />
            : v.jambase_venue_id
              ? <span className="font-mono">{v.jambase_venue_id}</span>
              : <span className="text-muted-foreground italic">auto-detected on first run</span>}
        </DLRow>
        <DLRow label="enabled:">
          {editing ? (
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={!!v.jambase_reaper_enabled} onChange={(e) => setDraft((d) => ({ ...d, jambase_reaper_enabled: e.target.checked }))} />
              <span>{v.jambase_reaper_enabled ? 'enabled' : 'disabled'}</span>
            </label>
          ) : (
            v.jambase_reaper_enabled ? '✓' : <span className="text-muted-foreground">—</span>
          )}
        </DLRow>
        <DLRow label="interval:">
          {editing
            ? <Input type="number" min="15" max="10080" value={v.jambase_reaper_interval_minutes} onChange={(e) => setDraft((d) => ({ ...d, jambase_reaper_interval_minutes: e.target.value }))} />
            : <span>{intervalMin} min <span className="text-muted-foreground">· {Math.round(intervalMin / 60 * 10) / 10}h</span></span>}
        </DLRow>
        <DLRow label="headliner allowlist:">
          {editing ? (
            <div>
              <Input
                value={v.jambase_headliner_allowlist}
                onChange={(e) => setDraft((d) => ({ ...d, jambase_headliner_allowlist: e.target.value }))}
                placeholder="calvin harris, tbd, …"
              />
              <div className="text-[10px] text-muted-foreground mt-0.5">comma-separated; case-insensitive substring match</div>
            </div>
          ) : (
            <ChipList items={v.jambase_headliner_allowlist} emptyText="none" />
          )}
        </DLRow>
        <DLRow label="vendor allowlist:">
          {editing ? (
            <VendorChips
              selected={v.jambase_delete_only_if_vendor_in || []}
              onChange={(arr) => setDraft((d) => ({ ...d, jambase_delete_only_if_vendor_in: arr }))}
            />
          ) : (
            <ChipList items={v.jambase_delete_only_if_vendor_in} emptyText="delete all vendors" />
          )}
        </DLRow>
        <DLRow label="only within (days):">
          {editing ? (
            <div>
              <Input
                type="number"
                min="1"
                max="3650"
                value={v.jambase_only_within_days ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, jambase_only_within_days: e.target.value }))}
                placeholder="no limit"
              />
              <div className="text-[10px] text-muted-foreground mt-0.5">skip events further out than N days</div>
            </div>
          ) : (
            v.jambase_only_within_days
              ? <span>{v.jambase_only_within_days} days</span>
              : <span className="text-muted-foreground italic">no limit</span>
          )}
        </DLRow>

        {/* Activity rows, separated by a hairline spanning both columns */}
        <div className="col-span-2 border-t border-border my-1" />
        <DLRow label="lifetime:">
          <span className="font-mono">{venue.lifetime_deleted_count ?? 0}</span> deleted
          <span className="text-muted-foreground"> · </span>
          <span className="font-mono">{venue.lifetime_skip_count ?? 0}</span> skipped
          <span className="text-muted-foreground"> · </span>
          <span className="font-mono">{venue.lifetime_dry_run_count ?? 0}</span> dry-run
        </DLRow>
        <DLRow label="last activity:">
          {venue.last_acted_at
            ? formatStamp(venue.last_acted_at, tz)
            : <span className="text-muted-foreground italic">none yet</span>}
        </DLRow>
      </div>

      {editing && (
        <div className="flex gap-2 mt-3">
          <button onClick={cancelEdit} disabled={saving} className="px-3 py-1 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50">Cancel</button>
          <button onClick={save} disabled={saving} className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      )}

      {err && <div className="mt-2 text-xs text-destructive">{err}</div>}

      {showLog && (
        <div className="mt-3 border-t border-border pt-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recent reaper activity</div>
            <button onClick={loadLog} className="text-[11px] text-muted-foreground hover:text-foreground">refresh</button>
          </div>
          {!log && <div className="text-xs text-muted-foreground">Loading…</div>}
          {log && log.length === 0 && <div className="text-xs text-muted-foreground italic">No reaper activity yet for this venue.</div>}
          {log && log.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="px-1 py-1 font-medium">When</th>
                    <th className="px-1 py-1 font-medium">Action</th>
                    <th className="px-1 py-1 font-medium">Event</th>
                    <th className="px-1 py-1 font-medium">Headliner</th>
                    <th className="px-1 py-1 font-medium">Date</th>
                    <th className="px-1 py-1 font-medium">Ticket URL</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((row) => (
                    <tr key={row.id} className="border-t border-border align-top">
                      <td className="px-1 py-1 text-muted-foreground whitespace-nowrap">{formatStamp(row.acted_at, tz)}</td>
                      <td className="px-1 py-1">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className={
                            row.action === 'delete' && row.success ? 'text-emerald-700 dark:text-emerald-400' :
                            row.action === 'dry_run'                ? 'text-amber-700 dark:text-amber-300' :
                            row.action === 'skip'                   ? 'text-muted-foreground' :
                            'text-destructive'
                          }>{row.action}{row.success === false ? ' ✗' : ''}</span>
                          {row.skip_reason && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-muted font-mono">{row.skip_reason}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-1 py-1 font-mono">{row.jambase_event_id}</td>
                      <td className="px-1 py-1">{row.headliner_name || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-1 py-1 text-muted-foreground">{row.event_date || ''} {row.event_time || ''}</td>
                      <td className="px-1 py-1 max-w-[18rem]">
                        {row.primary_ticketing_url ? (
                          <a href={row.primary_ticketing_url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate inline-block max-w-full" title={row.primary_ticketing_url}>
                            {new URL(row.primary_ticketing_url).host}
                          </a>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
